"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const brands = ["Doosan/DN Solutions", "Mori Seiki", "Haas", "Matsuura", "Tsugami", "Nakamura-Tome", "Okuma", "Mazak", "Kuraki", "Hwacheon", "Daewoo", "Fadal", "Nomura", "Star", "Miyano", "Toyoda", "Johnford", "Hitachi Seiki", "Ganesh", "Fanuc RoboDrill", "Hardinge"];
function BrandBadge({
  brand,
  index
}: {
  brand: string;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("group relative flex aspect-[3/2] items-center justify-center overflow-hidden border border-background/10 bg-background/5 p-4 backdrop-blur-md transition-all duration-700 md:p-6", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0", "hover:border-chart-1 hover:bg-chart-1/10")} style={{
    transitionDelay: `${index * 50}ms`
  }}>
      {}
      <div className="absolute left-0 top-0 h-2 w-2 border-l border-t border-background/30 transition-colors duration-500 group-hover:border-chart-1" />
      <div className="absolute bottom-0 right-0 h-2 w-2 border-b border-r border-background/30 transition-colors duration-500 group-hover:border-chart-1" />
      
      <span className="text-center font-mono text-sm font-bold uppercase tracking-wider text-background/80 transition-colors duration-500 group-hover:text-chart-1 md:text-base">
        {brand}
      </span>
    </div>;
}
export default function MachineToolBrands() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section id="machinetoolbrands" className="bg-background">
      <div className="w-full bg-foreground py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div ref={headerRef} className={cn("mx-auto mb-16 max-w-4xl text-center transition-all duration-1000", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
            <h2 className="font-sans text-3xl font-bold uppercase tracking-tighter text-background md:text-5xl lg:text-6xl">
              A Few Machine Tool Brands <br className="hidden md:block" />
              <span className="text-chart-1">we Have Extensive Experience With</span>
            </h2>
            <div className="mx-auto mt-8 h-1 w-24 bg-chart-1/50" />
          </div>

          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 lg:gap-4">
            {brands.map((brand, index) => <BrandBadge key={brand} brand={brand} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}